import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiShield, FiDatabase, FiUsers, FiSettings, FiTrendingUp, FiCheckCircle, FiArrowRight, FiClock, FiDollarSign, FiLock, FiEye } = FiIcons;

const ServicesPage = () => {
  const services = [
    {
      icon: FiDatabase,
      title: 'Data Governance',
      description: 'Transform your organization\'s data into a trusted, strategic asset with comprehensive governance frameworks',
      features: [
        'Data catalog management',
        'Data quality frameworks',
        'Data stewardship programs',
        'Governance analytics',
        'Automated policy enforcement'
      ],
      price: 'Starting at $8,000',
      timeline: '3-6 weeks',
      link: '/data-governance'
    },
    {
      icon: FiShield,
      title: 'Compliance Management',
      description: 'Navigate complex regulatory requirements with automated compliance monitoring and reporting',
      features: [
        'GDPR compliance automation',
        'HIPAA protection measures',
        'CCPA/CPRA fulfillment',
        'SOX financial controls',
        'Audit readiness preparation'
      ],
      price: 'Starting at $6,000',
      timeline: '2-4 weeks',
      link: '/compliance-management'
    },
    {
      icon: FiLock,
      title: 'Data Loss Prevention',
      description: 'Protect sensitive data from unauthorized access and exfiltration with advanced DLP solutions',
      features: [
        'Email security monitoring',
        'Document protection',
        'Database security controls',
        'Network traffic monitoring',
        'Real-time threat detection'
      ],
      price: 'Starting at $7,000',
      timeline: '3-5 weeks',
      link: '/data-loss-prevention'
    },
    {
      icon: FiEye,
      title: 'Information Protection',
      description: 'Enterprise-grade protection for your most valuable information with advanced security controls',
      features: [
        'Military-grade encryption',
        'Rights management',
        'Key lifecycle management',
        'AI-powered classification',
        'Dynamic data masking'
      ],
      price: 'Starting at $9,000',
      timeline: '4-6 weeks',
      link: '/information-protection'
    },
    {
      icon: FiUsers,
      title: 'Training & Certification',
      description: 'Comprehensive training programs for your team on Microsoft Purview best practices',
      features: [
        'Administrator training',
        'End-user workshops',
        'Best practices sessions',
        'Certification preparation',
        'Ongoing support materials'
      ],
      price: 'Starting at $3,000',
      timeline: '1-2 weeks'
    },
    {
      icon: FiSettings,
      title: 'Custom Integration',
      description: 'Seamless integration with your existing technology stack and third-party systems',
      features: [
        'API development and integration',
        'Third-party system connections',
        'Custom workflow automation',
        'Legacy system migration',
        'Performance optimization'
      ],
      price: 'Custom pricing',
      timeline: '4-8 weeks'
    }
  ];

  const processSteps = [
    { step: '01', title: 'Discovery & Assessment', description: 'We analyze your current data landscape and identify opportunities for improvement.', duration: '1 week' },
    { step: '02', title: 'Strategy Development', description: 'Create a customized roadmap tailored to your specific business requirements.', duration: '1-2 weeks' },
    { step: '03', title: 'Implementation', description: 'Deploy Microsoft Purview with minimal disruption to your operations.', duration: '2-6 weeks' },
    { step: '04', title: 'Training & Handover', description: 'Comprehensive training for your team and smooth transition to production.', duration: '1-2 weeks' },
    { step: '05', title: 'Ongoing Support', description: 'Continuous monitoring, optimization, and support to ensure success.', duration: 'Ongoing' }
  ];

  return (
    <div className="py-16">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-900 to-purple-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-5xl font-bold mb-6">
                Microsoft Purview Services That Drive Results
              </h1>
              <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
                From implementation to optimization, we provide end-to-end Microsoft Purview services that transform your data governance and ensure compliance success.
              </p>
              <Link
                to="/purview-audit"
                className="inline-flex items-center bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg text-xl font-bold transition-all transform hover:scale-105"
              >
                Get Your Purview Assessment
                <SafeIcon icon={FiArrowRight} className="ml-2" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Main Services Grid */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Core Data Governance Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specialized services designed to meet your unique data governance and compliance needs
            </p>
          </div>
          <div className="grid lg:grid-cols-2 gap-8 mb-16">
            {services.slice(0, 4).map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="flex items-start space-x-4 mb-6">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <SafeIcon icon={service.icon} className="h-8 w-8 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{service.title}</h3>
                    <p className="text-gray-600">{service.description}</p>
                  </div>
                </div>
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3">What's Included:</h4>
                  <ul className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center">
                        <SafeIcon icon={FiCheckCircle} className="text-green-500 mr-3 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="flex justify-between items-center pt-6 border-t border-gray-200">
                  <div>
                    <div className="text-2xl font-bold text-blue-600">{service.price}</div>
                    <div className="text-sm text-gray-500 flex items-center">
                      <SafeIcon icon={FiClock} className="mr-1" />
                      {service.timeline}
                    </div>
                  </div>
                  <Link
                    to={service.link}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                  >
                    Learn More
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Additional Services */}
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-6">Additional Services</h3>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Complementary services to enhance your Microsoft Purview implementation
            </p>
          </div>
          <div className="grid lg:grid-cols-2 gap-8">
            {services.slice(4).map((service, index) => (
              <motion.div
                key={index + 4}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="flex items-start space-x-4 mb-6">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <SafeIcon icon={service.icon} className="h-8 w-8 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{service.title}</h3>
                    <p className="text-gray-600">{service.description}</p>
                  </div>
                </div>
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3">What's Included:</h4>
                  <ul className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center">
                        <SafeIcon icon={FiCheckCircle} className="text-green-500 mr-3 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="flex justify-between items-center pt-6 border-t border-gray-200">
                  <div>
                    <div className="text-2xl font-bold text-blue-600">{service.price}</div>
                    <div className="text-sm text-gray-500 flex items-center">
                      <SafeIcon icon={FiClock} className="mr-1" />
                      {service.timeline}
                    </div>
                  </div>
                  <Link
                    to="/contact"
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                  >
                    Learn More
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Our Proven 5-Step Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We follow a systematic approach that ensures successful implementation and long-term success
            </p>
          </div>
          <div className="relative">
            {/* Connection Line */}
            <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-blue-200 transform -translate-y-1/2"></div>
            <div className="grid lg:grid-cols-5 gap-8">
              {processSteps.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="relative text-center"
                >
                  {/* Step Number */}
                  <div className="bg-blue-600 text-white w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6 relative z-10">
                    {step.step}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                  <p className="text-gray-600 mb-4">{step.description}</p>
                  <div className="text-sm text-blue-600 font-semibold">{step.duration}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-6">
            Ready to Transform Your Data Governance?
          </h2>
          <p className="text-xl mb-8 text-blue-100">
            Get started with a consultation and discover how Microsoft Purview can revolutionize your data management
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/purview-audit"
              className="inline-flex items-center bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg text-lg font-bold transition-all transform hover:scale-105"
            >
              Get Purview Assessment
              <SafeIcon icon={FiArrowRight} className="ml-2" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-100 transition-colors"
            >
              Schedule Consultation
            </Link>
          </div>
          <p className="text-sm mt-6 text-blue-200">
            ✓ No obligations ✓ Expert consultation ✓ Customized recommendations
          </p>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;